import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Divider,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon,
  Timeline as TimelineIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  ExitToApp as ExitIcon,
  WarningAmber as WarningIcon,
  CalendarMonth as CalendarIcon,
  Assessment as AssessmentIcon,
  ShowChart as ShowChartIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ReferenceLine,
} from 'recharts';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { RiskBudgetNotice } from '../../components/options/RiskBudgetNotice';
import {
  optionsService,
  CommandCenterData,
  ActiveCampaign,
  CampaignLeg,
  ClosedCampaign,
} from '../../services/optionsService';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';

// --- Price Range Visual Bar Component ---
interface PriceRangeBarProps {
  currentSpot: number;
  shortStrike: number;
  wingStrike: number;
  structuralStop: number;
  isBullish: boolean;
}

const PriceRangeBar: React.FC<PriceRangeBarProps> = ({
  currentSpot,
  shortStrike,
  wingStrike,
  structuralStop,
  isBullish,
}) => {
  const hasLiveSpot = currentSpot > 0;
  const effectiveSpot = hasLiveSpot ? currentSpot : shortStrike;

  const validPrices = [effectiveSpot, shortStrike, wingStrike, structuralStop].filter((p) => p > 0);
  if (validPrices.length < 2) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Short Strike: {shortStrike} | Wing: {wingStrike} {hasLiveSpot ? `| Spot: ₹${currentSpot.toLocaleString()}` : ''}
        </Typography>
      </Box>
    );
  }

  const minPrice = Math.min(...validPrices) - 80;
  const maxPrice = Math.max(...validPrices) + 80;
  const span = maxPrice - minPrice || 1;

  const getPercent = (price: number) => {
    return Math.min(100, Math.max(0, ((price - minPrice) / span) * 100));
  };

  const spotPct = getPercent(effectiveSpot);
  const shortPct = getPercent(shortStrike);
  const wingPct = getPercent(wingStrike);
  const stopPct = getPercent(structuralStop);

  const bufferPoints = hasLiveSpot
    ? isBullish
      ? currentSpot - shortStrike
      : shortStrike - currentSpot
    : 0;
  const isSafe = bufferPoints > 0;

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Title & Buffer Badge */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2 }}>
        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, letterSpacing: 0.5 }}>
          STRUCTURAL PRICE BOUNDS & SPOT PROXIMITY
        </Typography>
        {hasLiveSpot ? (
          <Chip
            size="small"
            label={`${isSafe ? '🛡️ Safe Buffer' : '⚠️ In The Money'}: ${Math.abs(bufferPoints).toFixed(1)} pts`}
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 700,
              bgcolor: isSafe ? '#DCFCE7' : '#FEE2E2',
              color: isSafe ? '#16A34A' : '#DC2626',
              border: `1px solid ${isSafe ? '#86EFAC' : '#FCA5A5'}`,
            }}
          />
        ) : (
          <Chip
            size="small"
            label="⏸️ Market Closed / Awaiting Tick"
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 600,
              bgcolor: '#F1F5F9',
              color: '#64748B',
              border: '1px solid #CBD5E1',
            }}
          />
        )}
      </Box>

      {/* Visual Bar Track */}
      <Box
        sx={{
          position: 'relative',
          height: 12,
          bgcolor: '#E2E8F0',
          borderRadius: 2,
          overflow: 'visible',
          border: '1px solid #CBD5E1',
        }}
      >
        {/* Safe Profit Zone Highlight */}
        {hasLiveSpot && (
          <Box
            sx={{
              position: 'absolute',
              left: `${Math.min(shortPct, spotPct)}%`,
              width: `${Math.abs(spotPct - shortPct)}%`,
              height: '100%',
              bgcolor: isSafe ? 'rgba(22, 163, 74, 0.25)' : 'rgba(220, 38, 38, 0.25)',
              borderRadius: 1,
            }}
          />
        )}

        {/* Wing Strike Marker */}
        {wingStrike > 0 && (
          <Tooltip title={`Wing Protection Strike: ₹${wingStrike.toLocaleString()}`}>
            <Box
              sx={{
                position: 'absolute',
                left: `${wingPct}%`,
                top: -3,
                bottom: -3,
                width: 3,
                bgcolor: '#2563EB',
                borderRadius: 1,
                zIndex: 2,
              }}
            />
          </Tooltip>
        )}

        {/* Short Strike Marker */}
        {shortStrike > 0 && (
          <Tooltip title={`Short Strike: ₹${shortStrike.toLocaleString()}`}>
            <Box
              sx={{
                position: 'absolute',
                left: `${shortPct}%`,
                top: -5,
                bottom: -5,
                width: 3,
                bgcolor: '#D97706',
                borderRadius: 1,
                zIndex: 3,
              }}
            />
          </Tooltip>
        )}

        {/* Structural Stop Marker */}
        {structuralStop > 0 && (
          <Tooltip title={`Structural Stop: ₹${structuralStop.toLocaleString()}`}>
            <Box
              sx={{
                position: 'absolute',
                left: `${stopPct}%`,
                top: -5,
                bottom: -5,
                width: 3,
                bgcolor: '#DC2626',
                borderRadius: 1,
                zIndex: 3,
              }}
            />
          </Tooltip>
        )}

        {/* Current Spot Needle */}
        {hasLiveSpot && (
          <Tooltip title={`Current Spot: ₹${currentSpot.toLocaleString()}`}>
            <Box
              sx={{
                position: 'absolute',
                left: `${spotPct}%`,
                top: -8,
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 5,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: '#0284C7',
                  borderRadius: '50%',
                  border: '2px solid #FFFFFF',
                  boxShadow: '0 0 6px rgba(2, 132, 199, 0.6)',
                }}
              />
              <Box sx={{ width: 2, height: 14, bgcolor: '#0284C7' }} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Markers Legend Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, fontSize: '0.72rem' }}>
        <Typography variant="caption" sx={{ color: '#2563EB', fontWeight: 700 }}>
          🛡️ Wing: {wingStrike}
        </Typography>
        <Typography variant="caption" sx={{ color: '#DC2626', fontWeight: 700 }}>
          🛑 Stop: {structuralStop.toFixed(0)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#D97706', fontWeight: 700 }}>
          ⚡ Short: {shortStrike}
        </Typography>
        <Typography variant="caption" sx={{ color: hasLiveSpot ? '#0284C7' : '#64748B', fontWeight: 800 }}>
          {hasLiveSpot ? `📍 Spot: ${currentSpot.toFixed(1)}` : '📍 Spot: Offline'}
        </Typography>
      </Box>
    </Box>
  );
};

// --- Strategy Analysis Chart Modal (AlgoStraddle Style) ---
interface StrategyAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  campaign: ActiveCampaign | ClosedCampaign | null;
  spot: number;
}

const StrategyAnalysisModal: React.FC<StrategyAnalysisModalProps> = ({ open, onClose, campaign, spot }) => {
  const [tab, setTab] = useState<number>(0);

  // Hooks below must run on every render, so derive values null-safely and bail out after them.
  const legs = useMemo(() => campaign?.legs || [], [campaign]);
  const entryCredit = (campaign as any)?.entryCredit ?? (campaign as any)?.entryPremium ?? 0;
  const currentValue = (campaign as any)?.currentValue ?? entryCredit;
  const maxProfit = campaign?.maxProfit || 1;
  const maxLoss = campaign?.maxLoss || 1;

  // 1. AlgoStraddle-style Multi-day / Intraday Premium Decay Curve simulation points
  const holdingDays = campaign?.holdingDays || 0;
  const totalDte = (campaign as any)?.dteRemaining ? (campaign as any).dteRemaining + holdingDays : 10;
  
  const decayData = useMemo(() => {
    const points: any[] = [];
    const totalSteps = Math.max(6, Math.min(20, Math.round(totalDte)));
    const stepDays = totalDte / totalSteps;

    for (let i = 0; i <= totalSteps; i++) {
      const day = Number((i * stepDays).toFixed(1));
      const isPast = day <= holdingDays;
      
      // Theoretical quadratic theta decay curve (decay accelerates as T -> 0)
      const timeElapsedFrac = Math.min(1, day / totalDte);
      const theoreticalDecayFrac = 1 - Math.pow(1 - timeElapsedFrac, 1.8);
      const theoreticalValue = Number((entryCredit * (1 - theoreticalDecayFrac)).toFixed(2));
      
      // Actual / Realized trajectory up to today
      let actualValue: number | null = null;
      if (isPast) {
        if (holdingDays === 0 || day === 0) {
          actualValue = entryCredit;
        } else {
          const actualProgress = day / (holdingDays || 1);
          actualValue = Number((entryCredit - (entryCredit - currentValue) * actualProgress).toFixed(2));
        }
      }

      points.push({
        day: `Day ${day}`,
        actualValue,
        theoreticalValue,
        target50: Number((entryCredit * 0.5).toFixed(2)),
        entryCredit,
      });
    }
    return points;
  }, [entryCredit, currentValue, holdingDays, totalDte]);

  // 2. Analytical Payoff Curve across Spot Range
  const payoffData = useMemo(() => {
    if (!legs || legs.length === 0) return [];
    
    // Find min and max strikes
    const strikes = legs.map((l: any) => l.strike);
    const minStrike = Math.min(...strikes, spot > 0 ? spot : strikes[0]);
    const maxStrike = Math.max(...strikes, spot > 0 ? spot : strikes[0]);
    const center = spot > 0 ? spot : (minStrike + maxStrike) / 2;
    const rangeSpan = Math.max(800, (maxStrike - minStrike) * 3);
    const startPrice = Math.floor((center - rangeSpan / 2) / 50) * 50;
    const endPrice = Math.ceil((center + rangeSpan / 2) / 50) * 50;
    const step = 25;

    const curve: any[] = [];
    for (let testPrice = startPrice; testPrice <= endPrice; testPrice += step) {
      let expiryPnl = 0;
      for (const leg of legs) {
        const isCall = leg.optionType === 'CE';
        const intrinsic = isCall ? Math.max(0, testPrice - leg.strike) : Math.max(0, leg.strike - testPrice);
        const legCashflow = leg.side === 'BUY'
          ? (intrinsic - leg.entryPrice) * leg.quantity
          : (leg.entryPrice - intrinsic) * leg.quantity;
        expiryPnl += legCashflow;
      }

      curve.push({
        spot: testPrice,
        pnl: Number(expiryPnl.toFixed(2)),
        profitZone: expiryPnl >= 0 ? Number(expiryPnl.toFixed(2)) : 0,
        lossZone: expiryPnl < 0 ? Number(expiryPnl.toFixed(2)) : 0,
      });
    }
    return curve;
  }, [legs, spot]);

  if (!campaign) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1, bgcolor: '#FFFFFF', color: '#0F172A' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
            {campaign.strategyName} — Strategy Decay & Payoff Analytics
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            ID: {campaign.strategyId} | Underlying: {campaign.underlying} | Holding: {campaign.holdingDays} days
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#64748B' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: '1px solid #E2E8F0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, color: '#64748B', '&.Mui-selected': { color: '#0284C7' } },
            '& .MuiTabs-indicator': { bgcolor: '#0284C7' },
          }}
        >
          <Tab icon={<TrendingDownIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Premium Decay Curve (AlgoStraddle)" />
          <Tab icon={<ShowChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="At-Expiry Payoff Diagram" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* KPI Strip */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
          <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>ENTRY NET CREDIT</Typography>
            <Typography variant="body1" sx={{ color: '#16A34A', fontWeight: 800 }}>₹{entryCredit.toLocaleString()}</Typography>
          </Box>
          <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>CURRENT VALUE</Typography>
            <Typography variant="body1" sx={{ color: '#0284C7', fontWeight: 800 }}>₹{currentValue.toLocaleString()}</Typography>
          </Box>
          <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>MAX PROFIT</Typography>
            <Typography variant="body1" sx={{ color: '#16A34A', fontWeight: 800 }}>+₹{maxProfit.toLocaleString()}</Typography>
          </Box>
          <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>MAX DEFINED LOSS</Typography>
            <Typography variant="body1" sx={{ color: '#DC2626', fontWeight: 800 }}>-₹{maxLoss.toLocaleString()}</Typography>
          </Box>
        </Box>

        {/* Tab 0: Premium Decay Chart */}
        {tab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                COMBINED SPREAD PREMIUM DECAY TRAJECTORY
              </Typography>
              <Typography variant="caption" sx={{ color: '#16A34A', fontWeight: 700 }}>
                ● Actual Value vs ── Theoretical Decay
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={decayData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `₹${v}`} />
                  <ChartTooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', borderColor: '#E2E8F0', borderRadius: 8, color: '#0F172A' }}
                    formatter={(value: any, name?: any): any => [
                      value ? `₹${Number(value).toFixed(2)}` : 'Pending',
                      name === 'actualValue' ? 'Actual Spread Premium' : name === 'theoreticalValue' ? 'Theoretical Decay' : '50% Target',
                    ]}
                  />
                  <ReferenceLine y={entryCredit} stroke="#94A3B8" strokeDasharray="3 3" label="Entry Credit" />
                  <ReferenceLine y={entryCredit * 0.5} stroke="#16A34A" strokeDasharray="3 3" label="50% Profit Target" />
                  <Line type="monotone" dataKey="theoreticalValue" stroke="#94A3B8" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="actualValue" stroke="#0284C7" strokeWidth={3} dot={{ r: 4, fill: '#0284C7' }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'center', mt: 1 }}>
              As time progresses towards expiry, the net credit spread value decays towards ₹0, delivering maximum profit.
            </Typography>
          </Box>
        )}

        {/* Tab 1: At-Expiry Payoff Diagram */}
        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                AT-EXPIRY PROFIT & LOSS PAYOFF CURVE
              </Typography>
              <Typography variant="caption" sx={{ color: '#0284C7', fontWeight: 700 }}>
                {spot > 0 ? `Current Spot: ₹${spot.toFixed(1)}` : 'Payoff Range: ₹-3% to +3%'}
              </Typography>
            </Box>
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={payoffData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#16A34A" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#16A34A" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="lossGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC2626" stopOpacity={0} />
                      <stop offset="95%" stopColor="#DC2626" stopOpacity={0.3} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="spot" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `₹${v}`} />
                  <ChartTooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', borderColor: '#E2E8F0', borderRadius: 8, color: '#0F172A' }}
                    formatter={(value: any) => [`₹${Number(value).toFixed(2)}`, 'At-Expiry P&L']}
                  />
                  <ReferenceLine y={0} stroke="#475569" strokeWidth={1.5} />
                  {spot > 0 && <ReferenceLine x={spot} stroke="#0284C7" strokeWidth={2} label="Spot" />}
                  <Area type="monotone" dataKey="pnl" stroke="#0284C7" strokeWidth={2.5} fill="url(#profitGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {/* Legs Table */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>
          STRATEGY LEGS SPECIFICATION
        </Typography>
        <TableContainer sx={{ bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#F1F5F9' }}>
              <TableRow>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>SIDE</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>STRIKE & TYPE</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>QUANTITY</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>ENTRY PRICE</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>CURRENT PRICE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {legs.map((leg: any, i: number) => (
                <TableRow key={leg.legId || i}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={leg.side}
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        bgcolor: leg.side === 'SELL' ? '#FEE2E2' : '#DCFCE7',
                        color: leg.side === 'SELL' ? '#DC2626' : '#16A34A',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#0F172A', fontWeight: 700 }}>
                    {leg.strike} {leg.optionType}
                  </TableCell>
                  <TableCell sx={{ color: '#64748B' }}>{leg.quantity} ({leg.lotCount || 1} lot)</TableCell>
                  <TableCell sx={{ color: '#0F172A', fontWeight: 600 }}>₹{leg.entryPrice?.toFixed(2)}</TableCell>
                  <TableCell sx={{ color: '#0284C7', fontWeight: 600 }}>₹{leg.currentPrice?.toFixed(2) || leg.entryPrice?.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#0284C7', textTransform: 'none', fontWeight: 700 }}>
          Close Analysis
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Active Campaign Card Component ---
interface CampaignCardProps {
  campaign: ActiveCampaign;
  spot: number;
  onExit: (strategyId: string) => void;
  onOpenAnalysis: (campaign: ActiveCampaign) => void;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, spot, onExit, onOpenAnalysis }) => {
  const [expanded, setExpanded] = useState(false);
  const isPositive = campaign.unrealizedPnl >= 0;
  const isBullish = campaign.strategyName.toUpperCase().includes('BULL');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2.5,
        bgcolor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 3,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: '#CBD5E1',
          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {/* Header Row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={campaign.strategyName}
            sx={{
              fontWeight: 800,
              bgcolor: '#EFF6FF',
              color: '#2563EB',
              border: '1px solid #BFDBFE',
            }}
          />
          <Chip
            size="small"
            label={campaign.status}
            sx={{
              fontWeight: 700,
              fontSize: '0.72rem',
              bgcolor: campaign.status === 'OPEN' ? '#DCFCE7' : '#FEF3C7',
              color: campaign.status === 'OPEN' ? '#16A34A' : '#D97706',
              border: `1px solid ${campaign.status === 'OPEN' ? '#86EFAC' : '#FDE68A'}`,
            }}
          />
          <Chip
            size="small"
            icon={<HourglassIcon sx={{ '&&': { color: '#64748B', fontSize: 14 } }} />}
            label={`Holding: ${campaign.holdingDays}d`}
            sx={{ bgcolor: '#F1F5F9', color: '#475569', fontSize: '0.75rem', fontWeight: 600 }}
          />
          <Chip
            size="small"
            icon={<CalendarIcon sx={{ '&&': { color: '#64748B', fontSize: 14 } }} />}
            label={`Expiry: ${moment(campaign.expiry).format('DD MMM')} (${campaign.dteRemaining} DTE)`}
            sx={{ bgcolor: '#F1F5F9', color: '#475569', fontSize: '0.75rem', fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Unrealized P&L Badge */}
          <Box sx={{ textAlign: 'right', mr: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>
              UNREALIZED P&L
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: isPositive ? '#16A34A' : '#DC2626',
                lineHeight: 1.1,
              }}
            >
              {isPositive ? '+' : ''}₹{campaign.unrealizedPnl.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>

          {/* On-Click Strategy Analysis Modal Button (AlgoStraddle Style) */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<BarChartIcon />}
            onClick={() => onOpenAnalysis(campaign)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#0284C7',
              borderColor: '#BAE6FD',
              bgcolor: '#F0F9FF',
              '&:hover': { bgcolor: '#E0F2FE', borderColor: '#0284C7' },
            }}
          >
            Decay Chart
          </Button>

          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<ExitIcon />}
            onClick={() => onExit(campaign.strategyId)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: '#FECACA',
              color: '#DC2626',
              bgcolor: '#FEF2F2',
              '&:hover': { borderColor: '#EF4444', bgcolor: '#FEE2E2' },
            }}
          >
            Exit
          </Button>

          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: '#64748B', '&:hover': { color: '#0F172A', bgcolor: '#F1F5F9' } }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Horizontal Price Range Visualizer */}
      <Box sx={{ bgcolor: '#F8FAFC', p: 2, borderRadius: 2.5, mb: 2, border: '1px solid #E2E8F0' }}>
        <PriceRangeBar
          currentSpot={spot > 0 ? spot : campaign.spotVsRange?.currentSpot || 0}
          shortStrike={campaign.spotVsRange?.shortStrike || 0}
          wingStrike={campaign.spotVsRange?.wingStrike || 0}
          structuralStop={campaign.spotVsRange?.structuralStop || 0}
          isBullish={isBullish}
        />
      </Box>

      {/* Key Campaign Metrics & Theta Decay Progress */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 1.5 }}>
        <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
            ENTRY CREDIT / MAX PROFIT
          </Typography>
          <Typography variant="body1" sx={{ color: '#16A34A', fontWeight: 800, mt: 0.5 }}>
            ₹{campaign.entryPremium.toLocaleString()} / ₹{campaign.maxProfit.toLocaleString()}
          </Typography>
        </Box>

        <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
            CURRENT VALUE / MAX LOSS
          </Typography>
          <Typography variant="body1" sx={{ color: '#DC2626', fontWeight: 800, mt: 0.5 }}>
            ₹{campaign.currentValue.toLocaleString()} / ₹{campaign.maxLoss.toLocaleString()}
          </Typography>
        </Box>

        <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
            CAMPAIGN GREEKS (Δ / Θ)
          </Typography>
          <Typography variant="body1" sx={{ color: '#0F172A', fontWeight: 800, mt: 0.5 }}>
            Δ {campaign.greeks?.delta?.toFixed(3) || '0.000'} | Θ +₹{campaign.greeks?.theta?.toFixed(1) || '0.0'}
          </Typography>
        </Box>

        {/* Theta Decay Progress Bar */}
        <Box sx={{ bgcolor: '#F8FAFC', p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
              THETA DECAY CAPTURED
            </Typography>
            <Typography variant="caption" sx={{ color: '#0284C7', fontWeight: 800 }}>
              {campaign.thetaDecayProgress.toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={campaign.thetaDecayProgress}
            sx={{
              height: 8,
              borderRadius: 2,
              bgcolor: '#E2E8F0',
              '& .MuiLinearProgress-bar': {
                bgcolor: campaign.thetaDecayProgress >= 70 ? '#16A34A' : '#0284C7',
              },
            }}
          />
        </Box>
      </Box>

      {/* Expandable Legs Detail Table */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: '#E2E8F0', my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: 700, mb: 1 }}>
          POSITION LEGS & EXECUTION BREAKDOWN
        </Typography>
        <TableContainer sx={{ bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#F1F5F9' }}>
              <TableRow>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>SIDE</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>STRIKE & TYPE</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>QTY / LOTS</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>ENTRY PRICE</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>CURRENT LTP</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>LEG P&L</TableCell>
                <TableCell sx={{ color: '#475569', fontWeight: 700 }}>DELTA / THETA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaign.legs?.map((leg: CampaignLeg, idx: number) => {
                const legPnl = leg.pnl ?? (leg.side === 'SELL' ? (leg.entryPrice - leg.currentPrice) * leg.quantity : (leg.currentPrice - leg.entryPrice) * leg.quantity);
                const isLegPos = legPnl >= 0;
                return (
                  <TableRow key={leg.legId || idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={leg.side}
                        sx={{
                          height: 20,
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          bgcolor: leg.side === 'SELL' ? '#FEE2E2' : '#DCFCE7',
                          color: leg.side === 'SELL' ? '#DC2626' : '#16A34A',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#0F172A', fontWeight: 700 }}>
                      {leg.strike} {leg.optionType}
                    </TableCell>
                    <TableCell sx={{ color: '#64748B' }}>
                      {leg.quantity} ({leg.lotCount} lot)
                    </TableCell>
                    <TableCell sx={{ color: '#0F172A' }}>₹{leg.entryPrice?.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: '#0284C7', fontWeight: 600 }}>₹{leg.currentPrice?.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: isLegPos ? '#16A34A' : '#DC2626', fontWeight: 800 }}>
                      {isLegPos ? '+' : ''}₹{legPnl.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                      Δ {leg.greeks?.delta?.toFixed(3) || '—'} | Θ {leg.greeks?.theta?.toFixed(1) || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
};

// --- Main Options Dashboard Component ---
const OptionsDashboard: React.FC = () => {
  const [data, setData] = useState<CommandCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Strategy Analysis Modal State
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [selectedAnalysisCampaign, setSelectedAnalysisCampaign] = useState<ActiveCampaign | ClosedCampaign | null>(null);

  // Exit Confirmation Dialog State
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [selectedExitId, setSelectedExitId] = useState<string | null>(null);
  const [exitLoading, setExitLoading] = useState(false);

  const fetchCommandCenterData = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) setRefreshing(true);
      const res = await optionsService.getCommandCenter('NIFTY');
      if (res && res.success) {
        setData(res);
      }
    } catch (err: any) {
      console.error('Failed to fetch options command center:', err);
      if (isManualRefresh) toast.error('Failed to refresh options data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchCommandCenterData();
    const timer = setInterval(() => {
      fetchCommandCenterData();
    }, 8000);
    return () => clearInterval(timer);
  }, [fetchCommandCenterData]);

  const handleOpenExitDialog = (strategyId: string) => {
    setSelectedExitId(strategyId);
    setExitDialogOpen(true);
  };

  const handleOpenAnalysis = (campaign: ActiveCampaign | ClosedCampaign) => {
    setSelectedAnalysisCampaign(campaign);
    setAnalysisModalOpen(true);
  };

  const handleConfirmExit = async () => {
    if (!selectedExitId) return;
    try {
      setExitLoading(true);
      await optionsService.exitPosition(selectedExitId, 'MANUAL_DASHBOARD_EXIT');
      toast.success(`Position ${selectedExitId} exit signal submitted`);
      setExitDialogOpen(false);
      fetchCommandCenterData(true);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to exit position');
    } finally {
      setExitLoading(false);
    }
  };

  const greeks = data?.portfolioGreeks || {
    delta: 0,
    gamma: 0,
    theta: 0,
    vega: 0,
    maxDefinedRisk: 0,
  };

  const capital = data?.capitalSummary || {
    totalMargin: 500000,
    usedMargin: 0,
    freeMargin: 500000,
    strategyMix: {},
  };

  const marginUtilPct = capital.totalMargin > 0 ? (capital.usedMargin / capital.totalMargin) * 100 : 0;
  const currentLiveSpot = data?.structureSummary ? (data as any).currentSpot || 0 : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', color: '#0F172A', pb: 8 }}>
      {/* Light-Themed Header Bar */}
      <OptionsHeaderBar />
      <RiskBudgetNotice />

      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, pt: 1 }}>
        {/* Top Control Strip */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: -0.5 }}>
              Options Trading Command Center
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B' }}>
              Multi-day campaign lifecycle, portfolio Greeks, and capital risk management
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              size="small"
              label={data?.systemMode === 'LIVE' ? '🔴 LIVE' : '🟠 PAPER'}
              sx={{
                fontWeight: 800,
                fontSize: '0.75rem',
                bgcolor: data?.systemMode === 'LIVE' ? '#FEE2E2' : '#FFEDD5',
                color: data?.systemMode === 'LIVE' ? '#DC2626' : '#EA580C',
                border: `1px solid ${data?.systemMode === 'LIVE' ? '#EF4444' : '#F97316'}`,
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />}
              onClick={() => fetchCommandCenterData(true)}
              disabled={refreshing}
              sx={{
                textTransform: 'none',
                borderColor: '#CBD5E1',
                color: '#475569',
                bgcolor: '#FFFFFF',
                fontWeight: 700,
                '&:hover': { borderColor: '#94A3B8', bgcolor: '#F1F5F9' },
              }}
            >
              {refreshing ? 'Updating…' : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12 }}>
            <CircularProgress size={44} sx={{ color: '#0284C7', mb: 2 }} />
            <Typography variant="body2" sx={{ color: '#64748B' }}>
              Loading options campaigns and risk attribution…
            </Typography>
          </Box>
        ) : (
          <>
            {/* ========================================================================= */}
            {/* SECTION 1: PORTFOLIO GREEKS & RISK ATTRIBUTION STRIP                     */}
            {/* ========================================================================= */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                gap: 2,
                mb: 3.5,
              }}
            >
              {/* Net Delta */}
              <Paper sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                    NET DELTA (Δ)
                  </Typography>
                  <Box sx={{ p: 0.5, bgcolor: '#E0F2FE', borderRadius: 1.5, display: 'flex' }}>
                    <SpeedIcon sx={{ fontSize: 16, color: '#0284C7' }} />
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: Math.abs(greeks.delta) < 0.1 ? '#16A34A' : '#D97706' }}>
                  {greeks.delta > 0 ? '+' : ''}{greeks.delta.toFixed(4)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                  {Math.abs(greeks.delta) < 0.1 ? 'Direction Neutral' : greeks.delta > 0 ? 'Bullish Skew' : 'Bearish Skew'}
                </Typography>
              </Paper>

              {/* Net Theta */}
              <Paper sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                    NET THETA (Θ)
                  </Typography>
                  <Box sx={{ p: 0.5, bgcolor: '#DCFCE7', borderRadius: 1.5, display: 'flex' }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: '#16A34A' }} />
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: greeks.theta >= 0 ? '#16A34A' : '#DC2626' }}>
                  {greeks.theta >= 0 ? '+' : ''}₹{greeks.theta.toFixed(2)}/day
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                  Daily Time Decay Capture
                </Typography>
              </Paper>

              {/* Net Vega */}
              <Paper sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                    NET VEGA (ν)
                  </Typography>
                  <Box sx={{ p: 0.5, bgcolor: '#F3E8FF', borderRadius: 1.5, display: 'flex' }}>
                    <TimelineIcon sx={{ fontSize: 16, color: '#9333EA' }} />
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#9333EA' }}>
                  ₹{greeks.vega.toFixed(2)} / 1% IV
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                  Volatility Sensitivity
                </Typography>
              </Paper>

              {/* Net Gamma */}
              <Paper sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                    NET GAMMA (Γ)
                  </Typography>
                  <Box sx={{ p: 0.5, bgcolor: '#FEF3C7', borderRadius: 1.5, display: 'flex' }}>
                    <AssessmentIcon sx={{ fontSize: 16, color: '#D97706' }} />
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                  {greeks.gamma.toFixed(6)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                  Convexity Acceleration
                </Typography>
              </Paper>

              {/* Max Defined Risk */}
              <Paper sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                    MAX DEFINED RISK
                  </Typography>
                  <Box sx={{ p: 0.5, bgcolor: '#FEE2E2', borderRadius: 1.5, display: 'flex' }}>
                    <ShieldIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#DC2626' }}>
                  ₹{greeks.maxDefinedRisk.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                  Hard Capped Loss Limit
                </Typography>
              </Paper>
            </Box>

            {/* ========================================================================= */}
            {/* SECTION 2 & 3: ACTIVE CAMPAIGNS (LEFT) + EXPIRY & CAPITAL LADDER (RIGHT)  */}
            {/* ========================================================================= */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Left Column: Active Multi-Day Campaigns */}
              <Grid size={{ xs: 12, lg: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
                    Active Multi-Day Campaigns ({data?.activeCampaigns?.length || 0})
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                    Target holding period: 7 - 20 DTE
                  </Typography>
                </Box>

                {data?.activeCampaigns && data.activeCampaigns.length > 0 ? (
                  data.activeCampaigns.map((campaign) => (
                    <CampaignCard
                      key={campaign.strategyId}
                      campaign={campaign}
                      spot={currentLiveSpot}
                      onExit={handleOpenExitDialog}
                      onOpenAnalysis={handleOpenAnalysis}
                    />
                  ))
                ) : (
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      bgcolor: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                      borderRadius: 3,
                    }}
                  >
                    <ShieldIcon sx={{ fontSize: 48, color: '#0284C7', mb: 1.5, opacity: 0.8 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', mb: 0.5 }}>
                      No Active Campaigns Open
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', maxWidth: 480, mx: 'auto', mb: 2 }}>
                      The automated options trading engine is continuously evaluating multi-timeframe structure, volatility regimes, and gamma constraints.
                    </Typography>
                    {data?.structureSummary && (
                      <Stack spacing={0.75} alignItems="center">
                        <Chip
                          label={`Structure: ${data.structureSummary.scenario} · 15m ${data.structureSummary.primaryBias ?? '—'} ${
                            data.structureSummary.primaryEvent ?? ''
                          }${data.structureSummary.macroBias ? ` · 1h ${data.structureSummary.macroBias}` : ''}`}
                          sx={{
                            fontWeight: 700,
                            ...(data.structureSummary.degraded
                              ? { bgcolor: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A' }
                              : { bgcolor: '#F0F9FF', color: '#0284C7', border: '1px solid #BAE6FD' }),
                          }}
                        />
                        {data.structureSummary.scenario === 'NO_SETUP' && data.structureSummary.reason && (
                          <Typography variant="caption" sx={{ color: data.structureSummary.degraded ? '#B45309' : '#64748B', maxWidth: 520, textAlign: 'center' }}>
                            {data.structureSummary.degraded ? 'Market data degraded — ' : ''}
                            {data.structureSummary.reason}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Paper>
                )}
              </Grid>

              {/* Right Column: Expiry Ladder & Capital Allocation */}
              <Grid size={{ xs: 12, lg: 4 }}>
                {/* Expiry Ladder */}
                <Paper sx={{ p: 2.5, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, mb: 3, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarIcon sx={{ color: '#0284C7', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>
                      Expiry Ladder & Allocation
                    </Typography>
                  </Box>

                  {data?.expiryLadder && data.expiryLadder.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { color: '#64748B', fontWeight: 700, borderBottom: '1px solid #E2E8F0', py: 1 } }}>
                          <TableCell>EXPIRY</TableCell>
                          <TableCell align="center">DTE</TableCell>
                          <TableCell align="center">TRADES</TableCell>
                          <TableCell align="right">MAX RISK</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.expiryLadder.map((exp) => (
                          <TableRow key={exp.expiry} sx={{ '& td': { borderBottom: '1px solid #F1F5F9', py: 1.2 } }}>
                            <TableCell sx={{ color: '#0F172A', fontWeight: 700 }}>
                              {moment(exp.expiry).format('DD MMM YYYY')}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={`${exp.dte}d`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  fontWeight: 800,
                                  bgcolor: exp.dte <= 7 ? '#FEE2E2' : '#E0F2FE',
                                  color: exp.dte <= 7 ? '#DC2626' : '#0284C7',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ color: '#334155', fontWeight: 800 }}>
                              {exp.positionCount}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#DC2626', fontWeight: 800 }}>
                              ₹{exp.totalRisk.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', textAlign: 'center', py: 2 }}>
                      No open contract expiries
                    </Typography>
                  )}
                </Paper>

                {/* Capital Allocation Card */}
                <Paper sx={{ p: 2.5, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <WalletIcon sx={{ color: '#16A34A', fontSize: 20 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A' }}>
                      Capital & Margin Utilization
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                      MARGIN UTILIZED
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 800 }}>
                      ₹{capital.usedMargin.toLocaleString()} / ₹{capital.totalMargin.toLocaleString()} ({marginUtilPct.toFixed(1)}%)
                    </Typography>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={marginUtilPct}
                    sx={{
                      height: 8,
                      borderRadius: 2,
                      bgcolor: '#E2E8F0',
                      mb: 2,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: marginUtilPct > 80 ? '#DC2626' : marginUtilPct > 50 ? '#D97706' : '#16A34A',
                      },
                    }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #E2E8F0' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontWeight: 600 }}>
                        FREE MARGIN
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#16A34A', fontWeight: 800 }}>
                        ₹{capital.freeMargin.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontWeight: 600 }}>
                        ALLOCATION
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569', fontWeight: 700 }}>
                        {Object.keys(capital.strategyMix).length > 0
                          ? Object.entries(capital.strategyMix)
                              .map(([k, v]) => `${k.split(' ')[0]}: ${v}`)
                              .join(' | ')
                          : '100% Cash'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* ========================================================================= */}
            {/* SECTION 4: HISTORICAL CAMPAIGN LOGS, EQUITY CURVE & PERFORMANCE           */}
            {/* ========================================================================= */}
            <Paper sx={{ bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)' }}>
              <Box sx={{ borderBottom: '1px solid #E2E8F0', px: 2, bgcolor: '#F8FAFC' }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, val) => setActiveTab(val)}
                  sx={{
                    '& .MuiTab-root': {
                      color: '#64748B',
                      textTransform: 'none',
                      fontWeight: 700,
                      '&.Mui-selected': { color: '#0284C7' },
                    },
                    '& .MuiTabs-indicator': { bgcolor: '#0284C7' },
                  }}
                >
                  <Tab label={`Closed Campaigns (${data?.closedCampaigns?.length || 0})`} />
                  <Tab label="Cumulative Equity Curve" />
                  <Tab label="Strategy Win-Rates" />
                  <Tab label={`Stood-Down Setups (${data?.rejectedSetups?.length || 0})`} />
                </Tabs>
              </Box>

              {/* Tab 0: Closed Campaigns Log Table */}
              {activeTab === 0 && (
                <Box sx={{ p: 2 }}>
                  {data?.closedCampaigns && data.closedCampaigns.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                          <TableRow>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>STRATEGY</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>ENTRY DATE</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>EXIT DATE</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>HOLD DURATION</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>ENTRY CREDIT</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>REALIZED P&L</TableCell>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>OUTCOME</TableCell>
                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 700 }}>ACTION</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.closedCampaigns.map((c) => {
                            const isWin = c.realizedPnl >= 0;
                            return (
                              <TableRow key={c.strategyId} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                                <TableCell sx={{ color: '#0F172A', fontWeight: 700 }}>
                                  {c.strategyName}
                                </TableCell>
                                <TableCell sx={{ color: '#64748B' }}>
                                  {moment(c.entryTime).format('DD MMM YYYY HH:mm')}
                                </TableCell>
                                <TableCell sx={{ color: '#64748B' }}>
                                  {moment(c.exitTime).format('DD MMM YYYY HH:mm')}
                                </TableCell>
                                <TableCell sx={{ color: '#334155', fontWeight: 700 }}>
                                  {c.holdingDays} days
                                </TableCell>
                                <TableCell sx={{ color: '#0F172A', fontWeight: 600 }}>
                                  ₹{c.entryCredit.toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ color: isWin ? '#16A34A' : '#DC2626', fontWeight: 800 }}>
                                  {isWin ? '+' : ''}₹{c.realizedPnl.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={c.outcome}
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 800,
                                      bgcolor:
                                        c.outcome === 'MAX_PROFIT'
                                          ? '#DCFCE7'
                                          : c.outcome === 'STOP_LOSS'
                                          ? '#FEE2E2'
                                          : '#E0F2FE',
                                      color:
                                        c.outcome === 'MAX_PROFIT'
                                          ? '#16A34A'
                                          : c.outcome === 'STOP_LOSS'
                                          ? '#DC2626'
                                          : '#0284C7',
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenAnalysis(c)}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: '#0284C7' }}
                                  >
                                    View Chart
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        No closed campaigns recorded yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 1: Cumulative Equity Curve */}
              {activeTab === 1 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: 700, mb: 2 }}>
                    CUMULATIVE NET REALIZED P&L ACROSS CLOSED CAMPAIGNS
                  </Typography>
                  {data?.equityCurve && data.equityCurve.length > 0 ? (
                    <Box sx={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.equityCurve} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="pnlGradientLight" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#16A34A" stopOpacity={0.25} />
                              <stop offset="95%" stopColor="#16A34A" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                          <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                          <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `₹${v}`} />
                          <ChartTooltip
                            contentStyle={{ backgroundColor: '#FFFFFF', borderColor: '#E2E8F0', borderRadius: 8, color: '#0F172A' }}
                            formatter={(value: any) => [`₹${Number(value).toLocaleString()}`, 'Cumulative P&L']}
                          />
                          <Area type="monotone" dataKey="cumulativePnl" stroke="#16A34A" strokeWidth={2.5} fillOpacity={1} fill="url(#pnlGradientLight)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        Equity curve points will render as campaigns close.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 2: Strategy Stats Breakdown */}
              {activeTab === 2 && (
                <Box sx={{ p: 2 }}>
                  {data?.strategyStats && data.strategyStats.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#F1F5F9' }}>
                          <TableRow>
                            <TableCell sx={{ color: '#475569', fontWeight: 700 }}>STRATEGY</TableCell>
                            <TableCell align="center" sx={{ color: '#475569', fontWeight: 700 }}>CAMPAIGNS</TableCell>
                            <TableCell align="center" sx={{ color: '#475569', fontWeight: 700 }}>WIN RATE</TableCell>
                            <TableCell align="center" sx={{ color: '#475569', fontWeight: 700 }}>WINS / LOSSES</TableCell>
                            <TableCell align="center" sx={{ color: '#475569', fontWeight: 700 }}>AVG HOLD DURATION</TableCell>
                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 700 }}>TOTAL P&L</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.strategyStats.map((st) => (
                            <TableRow key={st.strategyName}>
                              <TableCell sx={{ color: '#0F172A', fontWeight: 700 }}>{st.strategyName}</TableCell>
                              <TableCell align="center" sx={{ color: '#334155', fontWeight: 600 }}>{st.totalCampaigns}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={`${st.winRate}%`}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.72rem',
                                    fontWeight: 800,
                                    bgcolor: st.winRate >= 60 ? '#DCFCE7' : '#FEF3C7',
                                    color: st.winRate >= 60 ? '#16A34A' : '#D97706',
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ color: '#64748B' }}>
                                {st.wins}W / {st.losses}L
                              </TableCell>
                              <TableCell align="center" sx={{ color: '#334155', fontWeight: 600 }}>
                                {st.avgHoldingDays} days
                              </TableCell>
                              <TableCell align="right" sx={{ color: st.totalPnl >= 0 ? '#16A34A' : '#DC2626', fontWeight: 800 }}>
                                {st.totalPnl >= 0 ? '+' : ''}₹{st.totalPnl.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        Strategy breakdown stats will appear after completed trades.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 3: Stood-Down Candidate Setups */}
              {activeTab === 3 && (
                <Box sx={{ p: 2 }}>
                  {data?.rejectedSetups && data.rejectedSetups.length > 0 ? (
                    <Stack spacing={1.5}>
                      {data.rejectedSetups.map((rej, idx) => (
                        <Paper
                          key={rej.id || idx}
                          sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 2 }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Chip
                              size="small"
                              label={rej.strategyName || 'SETUP EVALUATION'}
                              sx={{ bgcolor: '#E2E8F0', color: '#334155', fontWeight: 700 }}
                            />
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                              {moment(rej.time).format('HH:mm:ss')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#334155', mb: 1 }}>
                            {rej.reason || rej.summaryMessage}
                          </Typography>
                          {rej.gates && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {Object.entries(rej.gates).map(([gate, passed]: [string, any]) => (
                                <Chip
                                  key={gate}
                                  size="small"
                                  icon={passed ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                                  label={gate}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    bgcolor: passed ? '#DCFCE7' : '#FEE2E2',
                                    color: passed ? '#16A34A' : '#DC2626',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        No candidate setups rejected during current scan window.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </>
        )}
      </Box>

      {/* Strategy Decay & Payoff Modal (AlgoStraddle Style) */}
      <StrategyAnalysisModal
        open={analysisModalOpen}
        onClose={() => setAnalysisModalOpen(false)}
        campaign={selectedAnalysisCampaign}
        spot={currentLiveSpot}
      />

      {/* Manual Exit Confirmation Dialog */}
      <Dialog
        open={exitDialogOpen}
        onClose={() => !exitLoading && setExitDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', color: '#0F172A', borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#DC2626', fontWeight: 800 }}>
          <WarningIcon /> Confirm Manual Early Exit
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#64748B' }}>
            Are you sure you want to exit campaign <strong>{selectedExitId}</strong> early? All open legs will be squared off at market price.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setExitDialogOpen(false)}
            disabled={exitLoading}
            sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmExit}
            disabled={exitLoading}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#DC2626' }}
          >
            {exitLoading ? 'Submitting Exit…' : 'Confirm Early Exit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OptionsDashboard;
